// ══════════════════════════════════════════════════════════════════════════════
// POST CARD - Carte de post style Workplace by Meta (Version avancée)
// Supporte: Texte, Sondages, Événements, Tâches assignées, Documents avec preview
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MoreHorizontal, MessageCircle, Share2, Bookmark, Trash2,
  MapPin, Clock, Calendar, Check, X, ChevronDown, Send, ThumbsUp,
  CheckSquare, FileText, Download, ExternalLink, Flag, Users, AlertCircle
} from 'lucide-react';

// Réactions disponibles
const REACTIONS = [
  { type: 'like', emoji: '👍', label: "J'aime", color: '#2563eb' },
  { type: 'love', emoji: '❤️', label: 'Love', color: '#ef4444' },
  { type: 'haha', emoji: '😂', label: 'Haha', color: '#f59e0b' },
  { type: 'wow', emoji: '😮', label: 'Wow', color: '#f59e0b' },
  { type: 'sad', emoji: '😢', label: 'Triste', color: '#f59e0b' },
  { type: 'angry', emoji: '😡', label: 'Grrr', color: '#f97316' },
];

// Priorités de tâches
const TASK_PRIORITIES = {
  low: { label: 'Basse', color: '#22c55e', icon: '🟢', bg: '#dcfce7' },
  medium: { label: 'Moyenne', color: '#f59e0b', icon: '🟡', bg: '#fef3c7' },
  high: { label: 'Haute', color: '#ef4444', icon: '🔴', bg: '#fee2e2' },
  urgent: { label: 'Urgente', color: '#dc2626', icon: '🚨', bg: '#fecaca' },
};

// Statuts de tâche
const TASK_STATUSES = {
  pending: { label: 'À faire', color: '#6b7280', bg: '#f3f4f6' },
  in_progress: { label: 'En cours', color: '#3b82f6', bg: '#dbeafe' },
  completed: { label: 'Terminée', color: '#22c55e', bg: '#dcfce7' },
};

// Extensions de documents
const DOCUMENT_EXTENSIONS = {
  pdf: { icon: '📄', color: '#ef4444', label: 'PDF' },
  doc: { icon: '📝', color: '#2563eb', label: 'Word' },
  docx: { icon: '📝', color: '#2563eb', label: 'Word' },
  xls: { icon: '📊', color: '#22c55e', label: 'Excel' },
  xlsx: { icon: '📊', color: '#22c55e', label: 'Excel' },
  ppt: { icon: '📽️', color: '#f97316', label: 'PowerPoint' },
  pptx: { icon: '📽️', color: '#f97316', label: 'PowerPoint' },
  txt: { icon: '📃', color: '#6b7280', label: 'Texte' },
  csv: { icon: '📈', color: '#10b981', label: 'CSV' },
  zip: { icon: '📦', color: '#8b5cf6', label: 'Archive' },
};

// Formater la date relative
const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "À l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const PostCard = memo(function PostCard({
  post,
  currentUser,
  members = [],
  accentColor = '#E8650A',
  onReaction,
  onComment,
  onLoadComments,
  onPollVote,
  onEventResponse,
  onDelete,
  onTaskStatusChange
}) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [selectedPollOptions, setSelectedPollOptions] = useState([]);
  const [showDocPreview, setShowDocPreview] = useState(null);
  
  const isOwner = post.author_id === currentUser?.id || post.is_mine;
  const myReaction = REACTIONS.find(r => r.type === post.my_reaction);
  const totalReactions = post.reactions ? Object.values(post.reactions).reduce((a, b) => a + b, 0) : 0;
  
  // Helper pour format de taille
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };
  
  // Helper pour date relative
  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    
    if (days < 0) return `Il y a ${Math.abs(days)}j`;
    if (days === 0) return "Aujourd'hui";
    if (days === 1) return 'Demain';
    if (days < 7) return `Dans ${days}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Charger les commentaires
  const toggleComments = async () => {
    if (!showComments && comments.length === 0 && onLoadComments) {
      const loaded = await onLoadComments(post.id);
      setComments(loaded || []);
    }
    setShowComments(!showComments);
  };

  // Soumettre commentaire
  const submitComment = async () => {
    if (!newComment.trim() || !onComment) return;
    const success = await onComment(post.id, newComment);
    if (success) {
      setNewComment('');
      if (onLoadComments) {
        const loaded = await onLoadComments(post.id);
        setComments(loaded || []);
      }
    }
  };

  // Vote sondage
  const handlePollVote = () => {
    if (selectedPollOptions.length === 0 || !onPollVote) return;
    onPollVote(post.id, selectedPollOptions);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <div 
          className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
          style={{ backgroundColor: accentColor }}
        >
          {post.author_name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">{post.author_name || 'Utilisateur'}</p>
          <p className="text-sm text-gray-500">{formatTime(post.created_at)}</p>
        </div>
        
        {/* Menu */}
        {isOwner && (
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)} className="p-2 hover:bg-gray-100 rounded-full">
              <MoreHorizontal size={20} className="text-gray-500" />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-10 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-2 min-w-[140px]">
                  <button
                    onClick={() => { onDelete?.(post.id); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                  >
                    <Trash2 size={16} /> Supprimer
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      {post.content && (
        <div className="px-4 pb-3">
          <p className="text-gray-800 whitespace-pre-wrap">{post.content}</p>
        </div>
      )}

      {/* Poll - Charte graphique : fond gris clair, accents orange */}
      {post.poll && (
        <div className="mx-4 mb-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-3">{post.poll.question}</h3>
          <div className="space-y-2">
            {post.poll.options?.map((option) => {
              const pct = post.poll.total_votes > 0 ? Math.round((option.votes / post.poll.total_votes) * 100) : 0;
              const hasVoted = post.poll.my_votes?.length > 0;
              const isMyVote = post.poll.my_votes?.includes(option.id);
              
              return (
                <button
                  key={option.id}
                  onClick={() => !hasVoted && setSelectedPollOptions(post.poll.multiple 
                    ? (selectedPollOptions.includes(option.id) ? selectedPollOptions.filter(id => id !== option.id) : [...selectedPollOptions, option.id])
                    : [option.id]
                  )}
                  disabled={hasVoted}
                  className={`w-full relative overflow-hidden rounded-lg border-2 transition-all ${
                    selectedPollOptions.includes(option.id) ? 'border-orange-500 bg-orange-50' :
                    isMyVote ? 'border-orange-300 bg-orange-50' : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  {hasVoted && <div className="absolute inset-y-0 left-0 bg-orange-200/50" style={{ width: `${pct}%` }} />}
                  <div className="relative flex items-center justify-between px-4 py-3">
                    <span className={`text-sm ${isMyVote ? 'font-semibold text-orange-700' : 'text-gray-700'}`}>
                      {option.text} {isMyVote && '✓'}
                    </span>
                    {hasVoted && <span className="text-sm font-semibold text-orange-700">{pct}%</span>}
                  </div>
                </button>
              );
            })}
          </div>
          {!post.poll.my_votes?.length && selectedPollOptions.length > 0 && (
            <button onClick={handlePollVote} className="w-full mt-3 px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600">
              Voter
            </button>
          )}
          <p className="text-xs text-gray-500 mt-2">{post.poll.total_votes || 0} vote(s)</p>
        </div>
      )}

      {/* Event - Charte graphique : fond orange doux */}
      {post.event && (
        <div className="mx-4 mb-3 p-4 bg-orange-50 rounded-xl border border-orange-200">
          <div className="flex items-center gap-2 text-orange-600 text-sm font-medium mb-2">
            <Calendar size={16} /> Événement
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{post.event.title}</h3>
          {post.event.date && (
            <p className="flex items-center gap-2 text-sm text-gray-600 mb-1">
              <Clock size={14} />
              {new Date(post.event.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
          {post.event.location && (
            <p className="flex items-center gap-2 text-sm text-gray-600 mb-3">
              <MapPin size={14} /> {post.event.location}
            </p>
          )}
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => onEventResponse?.(post.id, 'going')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm ${
                post.event.my_response === 'going' ? 'bg-orange-500 text-white' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
              }`}
            >
              <Check size={14} className="inline mr-1" /> J'y vais ({post.event.going_count || 0})
            </button>
            <button
              onClick={() => onEventResponse?.(post.id, 'maybe')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm ${
                post.event.my_response === 'maybe' ? 'bg-gray-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Peut-être ({post.event.maybe_count || 0})
            </button>
            <button
              onClick={() => onEventResponse?.(post.id, 'not_going')}
              className={`px-4 py-2 rounded-lg text-sm ${
                post.event.my_response === 'not_going' ? 'bg-gray-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
      
      {/* Task */}
      {post.task && (
        <div className="mx-4 mb-3 p-4 bg-orange-50 rounded-xl border border-orange-200" data-testid="task-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckSquare size={18} className="text-orange-500" />
              <span className="font-medium text-orange-700 text-sm">Tâche</span>
              {post.task.priority && TASK_PRIORITIES[post.task.priority] && (
                <span 
                  className="px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ 
                    backgroundColor: TASK_PRIORITIES[post.task.priority].bg,
                    color: TASK_PRIORITIES[post.task.priority].color
                  }}
                >
                  {TASK_PRIORITIES[post.task.priority].icon} {TASK_PRIORITIES[post.task.priority].label}
                </span>
              )}
            </div>
            {post.task.status && TASK_STATUSES[post.task.status] && (
              <span 
                className="px-3 py-1 rounded-full text-xs font-semibold"
                style={{ 
                  backgroundColor: TASK_STATUSES[post.task.status].bg,
                  color: TASK_STATUSES[post.task.status].color
                }}
              >
                {TASK_STATUSES[post.task.status].label}
              </span>
            )}
          </div>
          
          <h3 className="text-lg font-bold text-gray-900 mb-2">{post.task.title}</h3>
          
          {post.task.description && (
            <p className="text-sm text-gray-600 mb-3">{post.task.description}</p>
          )}
          
          <div className="flex flex-wrap items-center gap-4 text-sm">
            {/* Date d'échéance */}
            {post.task.due_date && (
              <div className="flex items-center gap-1.5 text-gray-600">
                <Clock size={14} />
                <span className={`font-medium ${new Date(post.task.due_date) < new Date() ? 'text-red-500' : ''}`}>
                  {formatRelativeDate(post.task.due_date)}
                </span>
              </div>
            )}
            
            {/* Assignés */}
            {post.task.assignees && post.task.assignees.length > 0 && (
              <div className="flex items-center gap-1.5">
                <Users size={14} className="text-gray-500" />
                <div className="flex items-center gap-1">
                  {post.task.assignees.slice(0, 3).map((a, i) => (
                    <span 
                      key={a.id || i} 
                      className="inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium"
                    >
                      {a.name}
                    </span>
                  ))}
                  {post.task.assignees.length > 3 && (
                    <span className="text-gray-500 text-xs">+{post.task.assignees.length - 3}</span>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Actions de tâche */}
          {(post.task.assignees?.some(a => a.id === currentUser?.id) || isOwner) && (
            <div className="flex gap-2 mt-4">
              {post.task.status !== 'completed' && (
                <>
                  {post.task.status === 'pending' && (
                    <button
                      onClick={() => onTaskStatusChange?.(post.id, 'in_progress')}
                      className="flex-1 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg font-medium text-sm hover:bg-orange-200"
                    >
                      Commencer
                    </button>
                  )}
                  <button
                    onClick={() => onTaskStatusChange?.(post.id, 'completed')}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-200"
                  >
                    <Check size={14} className="inline mr-1" /> Terminer
                  </button>
                </>
              )}
              {post.task.status === 'completed' && (
                <button
                  onClick={() => onTaskStatusChange?.(post.id, 'pending')}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg font-medium text-sm hover:bg-gray-200"
                >
                  Rouvrir
                </button>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Documents */}
      {post.documents && post.documents.length > 0 && (
        <div className="mx-4 mb-3 space-y-2" data-testid="document-list">
          {post.documents.map((doc, i) => {
            const ext = doc.name?.split('.').pop()?.toLowerCase() || '';
            const docInfo = DOCUMENT_EXTENSIONS[ext] || { icon: '📁', color: '#6b7280', label: 'Fichier' };
            
            return (
              <div 
                key={doc.id || i} 
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors group cursor-pointer"
                onClick={() => setShowDocPreview(doc)}
              >
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl flex-shrink-0"
                  style={{ backgroundColor: `${docInfo.color}15` }}
                >
                  {docInfo.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 truncate">{doc.name}</p>
                  <p className="text-xs text-gray-500">{docInfo.label} {doc.size && `• ${formatFileSize(doc.size)}`}</p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <a 
                    href={doc.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-gray-700"
                    title="Ouvrir"
                  >
                    <ExternalLink size={16} />
                  </a>
                  <a 
                    href={doc.url} 
                    download={doc.name}
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-gray-700"
                    title="Télécharger"
                  >
                    <Download size={16} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Document Preview Modal */}
      <AnimatePresence>
        {showDocPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDocPreview(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <FileText size={20} className="text-gray-500" />
                  <span className="font-semibold text-gray-800">{showDocPreview.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <a 
                    href={showDocPreview.url} 
                    download={showDocPreview.name}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 flex items-center gap-2"
                  >
                    <Download size={16} /> Télécharger
                  </a>
                  <button 
                    onClick={() => setShowDocPreview(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>
              </div>
              
              {/* Preview content */}
              <div className="h-[70vh] bg-gray-100">
                {showDocPreview.name?.toLowerCase().endsWith('.pdf') ? (
                  <iframe 
                    src={showDocPreview.url}
                    className="w-full h-full border-0"
                    title="Document preview"
                  />
                ) : showDocPreview.preview_url ? (
                  <img 
                    src={showDocPreview.preview_url} 
                    alt={showDocPreview.name}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <FileText size={64} className="mb-4 text-gray-300" />
                    <p className="font-medium">Aperçu non disponible</p>
                    <p className="text-sm mt-2">Télécharge le fichier pour le consulter</p>
                    <a 
                      href={showDocPreview.url} 
                      download={showDocPreview.name}
                      className="mt-4 px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 flex items-center gap-2"
                    >
                      <Download size={18} /> Télécharger
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Media */}
      {post.media && post.media.length > 0 && (
        <div className={`grid gap-1 ${post.media.length === 1 ? '' : 'grid-cols-2'}`}>
          {post.media.slice(0, 4).map((m, i) => (
            <div key={i} className="relative">
              {m.type === 'video' ? (
                <video src={m.url} controls className="w-full h-48 object-cover" />
              ) : (
                <img src={m.url} alt="" className="w-full h-48 object-cover" />
              )}
              {i === 3 && post.media.length > 4 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">+{post.media.length - 4}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="px-4 py-2 flex items-center justify-between text-sm text-gray-500 border-b border-gray-100">
        <div className="flex items-center gap-1">
          {totalReactions > 0 && (
            <>
              {Object.entries(post.reactions || {}).filter(([_, c]) => c > 0).slice(0, 3).map(([type]) => {
                const r = REACTIONS.find(x => x.type === type);
                return r ? <span key={type}>{r.emoji}</span> : null;
              })}
              <span className="ml-1">{totalReactions}</span>
            </>
          )}
        </div>
        <button onClick={toggleComments} className="hover:underline">
          {post.comment_count || 0} commentaire{(post.comment_count || 0) > 1 ? 's' : ''}
        </button>
      </div>

      {/* Actions */}
      <div className="px-2 py-1 flex items-center border-b border-gray-100">
        {/* Reaction button */}
        <div className="flex-1 relative">
          <button
            onClick={() => onReaction?.(post.id, post.my_reaction ? null : 'like')}
            onMouseEnter={() => setShowReactions(true)}
            onMouseLeave={() => setTimeout(() => setShowReactions(false), 300)}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg transition-colors ${
              myReaction ? '' : 'text-gray-600 hover:bg-gray-100'
            }`}
            style={myReaction ? { color: myReaction.color } : {}}
          >
            {myReaction ? <span className="text-xl">{myReaction.emoji}</span> : <ThumbsUp size={20} />}
            <span className="text-sm font-medium">{myReaction?.label || "J'aime"}</span>
          </button>
          
          {/* Reaction picker */}
          <AnimatePresence>
            {showReactions && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1.5 bg-white rounded-full shadow-xl border flex gap-1 z-50"
                onMouseEnter={() => setShowReactions(true)}
                onMouseLeave={() => setShowReactions(false)}
              >
                {REACTIONS.map((r) => (
                  <button
                    key={r.type}
                    onClick={() => { onReaction?.(post.id, r.type); setShowReactions(false); }}
                    className="p-1 hover:scale-125 transition-transform text-2xl"
                    title={r.label}
                  >
                    {r.emoji}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        <button onClick={toggleComments} className="flex-1 flex items-center justify-center gap-2 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg">
          <MessageCircle size={20} />
          <span className="text-sm font-medium">Commenter</span>
        </button>
        <button className="flex-1 flex items-center justify-center gap-2 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg">
          <Share2 size={20} />
          <span className="text-sm font-medium">Partager</span>
        </button>
      </div>

      {/* Comments */}
      <AnimatePresence>
        {showComments && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="p-4 space-y-4">
              {/* Input */}
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ backgroundColor: accentColor }}>
                  {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 flex items-center gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && submitComment()}
                    placeholder="Écrire un commentaire..."
                    className="flex-1 px-4 py-2.5 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <button
                    onClick={submitComment}
                    disabled={!newComment.trim()}
                    className="p-2.5 rounded-full transition-colors disabled:opacity-40"
                    style={{ backgroundColor: newComment.trim() ? accentColor : '#e5e7eb', color: newComment.trim() ? 'white' : '#9ca3af' }}
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>

              {/* Comments list */}
              {comments.length === 0 && <p className="text-center text-gray-400 text-sm py-4">Aucun commentaire</p>}
              {comments.map((comment) => (
                <div key={comment.id} className="flex gap-3">
                  <div className="w-9 h-9 rounded-full bg-gray-300 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {comment.author_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1">
                    <div className="bg-gray-100 rounded-2xl px-4 py-2.5">
                      <p className="font-semibold text-sm text-gray-900">{comment.author_name}</p>
                      <p className="text-sm text-gray-700">{comment.content}</p>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 ml-2">{formatTime(comment.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

export default PostCard;
